import axios from "axios"
import { getFingerprint } from "../lib/fingerprint"
import { trackEvent } from "../lib/analytics"

axios.defaults.withCredentials = true

export const ALTAR_BACKEND_URL =
  process.env.NEXT_PUBLIC_ALTAR_BACKEND_URL?.replace(/['"]/g, "") ||
  "http://localhost:9000/api/v1"

// Ensure the URL is properly formatted
function sanitizeUrl(url: string): string {
  // Remove any trailing slashes
  return url.replace(/\/+$/, "")
}

// Store cookies globally (mostly for SSR if needed later)
let serverSideCookies: string | undefined = undefined

export function setServerSideCookies(cookies: string) {
  serverSideCookies = cookies
}

const client = axios.create({
  baseURL: sanitizeUrl(ALTAR_BACKEND_URL),
  withCredentials: true, // This is important for cross-domain cookie handling
})

// Event bus for global errors (like 402 Paywall)
export const apiEvents = new EventTarget()

const isServerSideContext = () => {
  return process.env.NEXT_RUNTIME === "nodejs" || typeof window === "undefined"
}

export default async function ocularRequest(
  method: string,
  path = "",
  payload: any = {},
  queryParams: any = {},
  download: boolean = false,
  token?: string | null // Added optional Clerk token
) {
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`
  const isFormData = payload instanceof FormData
  const isServerSide = process.env.NEXT_RUNTIME === "nodejs"

  let headers: any = {
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
      "ngrok-skip-browser-warning": "true",
  }

  // Add Clerk Token if provided
  if (token) {
      headers["Authorization"] = `Bearer ${token}`
  }

  // Admin impersonation — add header if impersonating a user (skip for /admin routes)
  if (typeof window !== 'undefined' && !sanitizedPath.startsWith('/admin')) {
      const impersonateUserId = localStorage.getItem('impersonate_user_id')
      if (impersonateUserId) {
          headers["X-Impersonate-User"] = impersonateUserId
      }
  }

  // Add Fingerprint if client-side and public endpoint
  if (!isServerSide && !token) {
      try {
          const fp = await getFingerprint()
          headers["X-Fingerprint"] = fp
      } catch (e) {
          console.warn("Could not get fingerprint", e)
      }
  }

  let options: any = {
    method,
    url: sanitizedPath,
    data: payload,
    responseType: download ? "blob" : "json",
    params: queryParams,
    headers,
    withCredentials: true,
  }

  return client(options).catch((error) => {
    // Global Error Handling
    if (error.response) {
        if (error.response.status === 402) {
            // Dispatch Paywall Event
            apiEvents.dispatchEvent(new CustomEvent("paywall-triggered"))
            trackEvent('paywall_hit', {
              trigger: 'payment_required',
              endpoint: path,
            })
        }

        if (error.response.status === 429) {
            // Insufficient credits / rate limit - parse detail and dispatch event
            const detail = error.response.data?.detail || {}
            const eventDetail = {
              message: detail.message || "Insufficient credits",
              creditType: detail.credit_type || undefined,
              required: detail.required || 0,
              available: detail.available || 0,
              upgradeUrl: detail.upgrade_url || "/pricing",
            }
            apiEvents.dispatchEvent(new CustomEvent("insufficient-credits", {
              detail: eventDetail
            }))
            trackEvent('paywall_hit', {
              trigger: 'insufficient_credits',
              credit_type: eventDetail.creditType,
              required: eventDetail.required,
              available: eventDetail.available,
              endpoint: path,
            })
        }
    }
    throw error
  })
}
